/**
 * Localization Controller
 * Handles country switching via Shopify's localization API
 * Manages auto-redirects and external store links
 */

class LocalizationController extends HTMLElement {
  constructor() {
    super();
  }

  async connectedCallback() {
    this.form = this.querySelector('.localization-controller__form');

    try {
      this.countryCode = await window.getCountryCode();
      this.localizationData = await window.getLocalizationData();

      // Check for auto-redirect
      if (!this.redirect()) {
        // Check for country_code URL parameter
        this.changeCountryByUrl();
      }
    } catch (error) {
      console.error('Localization controller error:', error);
    }
  }

  /**
   * Check if user should be auto-redirected
   * @returns {boolean} - True if redirected
   */
  redirect() {
    // Never redirect in theme editor
    if (window.Shopify && window.Shopify.designMode) {
      return false;
    }

    const countryData = this.getCountryData(this.countryCode);

    // If detected country has redirect flag = true
    if (countryData && countryData.redirect) {
      window.location.href = countryData.url;
      return true;
    }

    return false;
  }

  /**
   * Get country data by country code
   * IMPORTANT: Checks external stores FIRST, then Shopify markets
   * This ensures section blocks (with redirect URLs) take priority over market toggles
   * @param {string} countryCode - ISO country code
   * @returns {Object|null} - Country data object or null
   */
  getCountryData(countryCode) {
    // Check external stores first (section blocks with redirect functionality)
    const externalStore = this.localizationData.externalStores.find(
      country => country.code === countryCode
    );

    if (externalStore) {
      console.log('[Localization] Found in external stores:', countryCode, externalStore);
      return externalStore;
    }

    // Fall back to Shopify markets
    const shopifyMarket = this.localizationData.shopifyMarkets.find(
      country => country.code === countryCode
    );

    if (shopifyMarket) {
      console.log('[Localization] Found in Shopify markets:', countryCode, shopifyMarket);
      return shopifyMarket;
    }

    console.log('[Localization] Country not found:', countryCode);
    return null;
  }

  /**
   * Change country (called by global changeCountry function)
   * @param {string} countryCode - ISO country code
   */
  changeCountry(countryCode) {
    console.log('[Localization] changeCountry called with:', countryCode);
    const countryData = this.getCountryData(countryCode);

    if (!countryData) {
      console.error('[Localization] No country data found for:', countryCode);
      return;
    }

    // Custom country = external redirect (section blocks)
    if (countryData && countryData.isCustom) {
      console.log('[Localization] External store detected - redirecting to:', countryData.url);
      if (countryData.url) {
        // Set cookie before redirect
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + (7 * 24 * 60 * 60 * 1000));
        document.cookie = `country_selected=true; expires=${expirationDate.toUTCString()}; path=/`;

        window.location.href = countryData.url;
      }
      return;
    }

    // Shopify market = submit localization form
    console.log('[Localization] Shopify market detected - submitting localization form');
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + (7 * 24 * 60 * 60 * 1000));
    document.cookie = `country_selected=true; expires=${expirationDate.toUTCString()}; path=/`;

    this.form.country_code.value = countryCode;
    this.form.submit();
  }

  /**
   * Check for country_code URL parameter and change country if present
   */
  changeCountryByUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const countryCode = urlParams.get('country_code');

    if (!countryCode) return;

    const countryData = this.getCountryData(countryCode);

    // Only change if valid country and not custom and different from current
    if (
      countryData &&
      !countryData.isCustom &&
      countryData.code != this.localizationData.currentCountry
    ) {
      this.changeCountry(countryCode);
    } else {
      // Clean up invalid parameter from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('country_code');
      window.history.replaceState(null, document.title, url.toString());
    }
  }
}

// Register custom element
if (!customElements.get('localization-controller')) {
  customElements.define('localization-controller', LocalizationController);
}
